import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate } from "@/lib/format";
import { PageHeader, Card, Loading, Button, Modal, Field, TierBadge, Pill, EmptyState } from "@/components/kit";
import MemberCard from "@/components/MemberCard";

export default function Members() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = user.role === "owner" ? "/owner" : "/admin";
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [card, setCard] = useState(null);

  const openCard = async (e, m) => {
    e.stopPropagation();
    try {
      const memberships = await api.memberMemberships(m.id).catch(() => []);
      setCard({ member: m, memberships });
    } catch (err) { toast.error(err.message); }
  };

  const loadStats = () => api.ownerMembersStats().then(setStats).catch(() => {});
  const reload = () => { api.ownerMembers(q).then(setMembers).catch(() => setMembers([])); loadStats(); };
  useEffect(() => { loadStats(); }, []);
  // Server-side search — finds ANY registered member regardless of total count.
  useEffect(() => {
    let active = true;
    const t = setTimeout(() => {
      api.ownerMembers(q).then((r) => { if (active) setMembers(r); }).catch(() => { if (active) setMembers([]); });
    }, q ? 300 : 0);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  const filtered = useMemo(() => {
    if (!members) return [];
    if (statusFilter === "all") return members;
    return members.filter((m) => (m.membership_status || "inactive") === statusFilter);
  }, [members, statusFilter]);

  return (
    <div>
      <PageHeader eyebrow={user.role === "owner" ? "Owner" : "Admin"} title="Members"
        subtitle="Database member klub, tier, total belanja, dan poin."
        right={<Button onClick={() => setShowCreate(true)} data-testid="create-member-btn"><UserPlus className="h-4 w-4" /> Member Baru</Button>}
      />

      <div className="mb-6 flex flex-wrap items-end gap-8">
        <div>
          <div className="font-display text-5xl text-white" data-testid="members-count">{stats ? stats.total : "—"}</div>
          <div className="text-sm text-[#8E8E93]">Total Member Terdaftar</div>
        </div>
        <div>
          <div className="font-display text-2xl text-[#7CD992]" data-testid="members-active">{stats ? stats.active_membership : "—"}</div>
          <div className="text-xs text-[#8E8E93]">Membership Aktif</div>
        </div>
        <div>
          <div className="font-display text-2xl text-[#A1A1A6]" data-testid="members-inactive">{stats ? stats.inactive_membership : "—"}</div>
          <div className="text-xs text-[#8E8E93]">Tidak Aktif</div>
        </div>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5F]" />
        <input className="hsc-input pl-9" placeholder="Cari nama, nomor HP, atau Member ID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="member-search" />
      </div>
      <div className="mb-6 flex flex-wrap gap-2">
        {[["all", "Semua"], ["active", "Membership Aktif"], ["inactive", "Tidak Aktif"]].map(([v, l]) => (
          <Pill key={v} active={statusFilter === v} onClick={() => setStatusFilter(v)} data-testid={`member-filter-${v}`}>{l}</Pill>
        ))}
      </div>

      {!members ? <Loading /> : !filtered.length ? (
        <EmptyState icon={UserPlus} title="Tidak ada member" subtitle={q ? "Tidak ada hasil untuk pencarian ini." : "Tambahkan member pertama Anda."} action={!q && <Button onClick={() => setShowCreate(true)}>Member Baru</Button>} />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#1F1F22]">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0C] text-left text-[11px] uppercase tracking-widest text-[#6E6E73]">
              <tr>
                <th className="px-4 py-3 w-10 text-right">#</th>
                <th className="px-4 py-3">Nama</th>
                <th className="hidden px-4 py-3 lg:table-cell">Member ID</th>
                <th className="hidden px-4 py-3 md:table-cell">Terdaftar</th>
                <th className="hidden px-4 py-3 sm:table-cell">Akun</th>
                <th className="px-4 py-3">Membership</th>
                <th className="px-4 py-3 text-right">Belanja</th>
                <th className="hidden px-4 py-3 text-right sm:table-cell">Poin</th>
                <th className="px-4 py-3 text-right">Kartu</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={m.id} className="cursor-pointer border-t border-[#141416] hover:bg-[#0A0A0C]"
                  onClick={() => navigate(`${base}/members/${m.id}`)} data-testid="member-row">
                  <td className="px-4 py-3 text-right text-[11px] text-[#6E6E73]" data-testid={`member-num-${i + 1}`}>{i + 1}</td>
                  <td className="px-4 py-3">
                    <div className="text-white">{m.name}</div>
                    <div className="text-[11px] text-[#6E6E73]">{m.phone || "—"}</div>
                  </td>
                  <td className="hidden px-4 py-3 font-mono text-[11px] text-[#6E6E73] lg:table-cell" title={m.id}>{String(m.id).slice(0, 8)}</td>
                  <td className="hidden px-4 py-3 text-[#A1A1A6] md:table-cell">{m.created_at ? formatDate(m.created_at) : (m.member_since || "—")}</td>
                  <td className="hidden px-4 py-3 sm:table-cell">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${m.account_status === "login" ? "bg-[#10231a] text-[#7CD992]" : "bg-[#1A1A1A] text-[#8E8E93]"}`}>
                      {m.account_status === "login" ? "Punya Login" : "Tanpa Login"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${m.membership_status === "active" ? "bg-[#10231a] text-[#7CD992]" : "bg-[#1A1A1A] text-[#8E8E93]"}`} data-testid={`member-ms-${m.id}`}>
                      {m.membership_status === "active" ? "Aktif" : "Tidak Aktif"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-[#D1D1D6]">{formatIDR(m.lifetime_spend)}</td>
                  <td className="hidden px-4 py-3 text-right font-medium text-white sm:table-cell">{m.points}</td>
                  <td className="px-4 py-3 text-right">
                    <button className="rounded-lg p-1.5 text-[#8E8E93] hover:bg-[#141416] hover:text-white"
                      onClick={(e) => openCard(e, m)} title="Member Card" data-testid={`member-card-btn-${m.id}`}>
                      <QrCode className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); reload(); }} />}

      {card && (
        <Modal open title="Member Card" onClose={() => setCard(null)} maxWidth="max-w-md">
          <MemberCard member={card.member} memberships={card.memberships} />
        </Modal>
      )}
    </div>
  );
}

function CreateModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim() || !f.phone.trim()) return toast.error("Nama & nomor HP wajib diisi");
    setBusy(true);
    try {
      await api.ownerCreateMember({ name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim() || undefined, password: f.password || undefined });
      toast.success("Member dibuat"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open title="Member Baru" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Nama"><input className="hsc-input" value={f.name} onChange={set("name")} data-testid="new-member-name" /></Field>
        <Field label="Nomor HP (WhatsApp)"><input className="hsc-input" value={f.phone} onChange={set("phone")} placeholder="0812xxxx" data-testid="new-member-phone" /></Field>
        <Field label="Email (opsional)"><input className="hsc-input" value={f.email} onChange={set("email")} /></Field>
        <Field label="Password (opsional — buat akun login)"><input type="password" className="hsc-input" value={f.password} onChange={set("password")} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy} data-testid="new-member-save">Simpan</Button>
        </div>
      </div>
    </Modal>
  );
}
